import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import NicheModal from "./NicheModal";

export type PricingTier = "spark" | "launch" | "backbone";
export type HighlightedTier = PricingTier | null;

interface PricingSectionProps {
  highlightedTier?: HighlightedTier;
  onCTA?: () => void;
}

interface TierConfig {
  key: PricingTier;
  featureKeys: string[];
  hasStartingFrom: boolean;
  geoOnFeature?: string;
}

const TIERS: TierConfig[] = [
  {
    key: "spark",
    featureKeys: ["spark_f1", "spark_f2", "spark_f3", "spark_f4"],
    hasStartingFrom: true,
  },
  {
    key: "launch",
    featureKeys: ["launch_f1", "launch_f2", "launch_f3", "launch_f4", "launch_f5"],
    hasStartingFrom: true,
  },
  {
    key: "backbone",
    featureKeys: ["backbone_f1", "backbone_f2", "backbone_f3", "backbone_f4", "backbone_f5", "backbone_f6"],
    hasStartingFrom: false,
    geoOnFeature: "backbone_f6",
  },
];

export default function PricingSection({ highlightedTier = "launch", onCTA }: PricingSectionProps) {
  const { t } = useTranslation();
  const [modalTier, setModalTier] = useState<PricingTier | null>(null);

  const handleCTA = (tier: PricingTier) => {
    onCTA?.();
    setModalTier(tier);
  };

  return (
    <section className="dyna-pricing" id="pricing">
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <h2
          style={{
            fontFamily: "Coolvetica, system-ui, sans-serif",
            fontSize: "clamp(32px, 4vw, 48px)",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            lineHeight: 1.1,
            margin: "0 0 12px",
            color: "var(--nova-600)",
          }}
        >
          {t("dyna.pricing.heading")}
        </h2>
      </div>

      <div className="dyna-pricing__grid">
        {TIERS.map((tier) => {
          const isFeatured = highlightedTier === tier.key;
          const name = t(`dyna.pricing.${tier.key}_name`);
          const priceRaw = t(`dyna.pricing.${tier.key}_price`);
          const isCustom = tier.key === "backbone";
          const note = t(`dyna.pricing.${tier.key}_note`);
          const ctaLabel = t(`dyna.pricing.${tier.key}_cta`);
          const currency = t("dyna.pricing.currency");
          const accessibleName = isFeatured
            ? `${name} plan, ${t("dyna.pricing.badge")}`
            : `${name} plan`;

          return (
            <article
              key={tier.key}
              role="article"
              aria-label={accessibleName}
              className={`dyna-tier${isFeatured ? " dyna-tier--featured" : ""}`}
            >
              {isFeatured && (
                <div className="dyna-tier__badge" aria-hidden="true">
                  {t("dyna.pricing.badge")}
                </div>
              )}

              <h3 className="dyna-tier__name">{name}</h3>

              <div>
                {tier.hasStartingFrom && (
                  <p className="dyna-tier__starting">{t("dyna.pricing.starting_from")}</p>
                )}
                <div className="dyna-tier__price">
                  <span className="dyna-tier__amt">
                    {isCustom ? priceRaw : `${currency}${priceRaw}`}
                  </span>
                  <span className="dyna-tier__note">{note}</span>
                </div>
              </div>

              <ul className="dyna-tier__features">
                {tier.featureKeys.map((fk) => {
                  const label = t(`dyna.pricing.${fk}`);
                  const showGeoTooltip = tier.geoOnFeature === fk;
                  return (
                    <li key={fk}>
                      <span className="dyna-tier__check" aria-hidden="true">→</span>
                      <span>
                        {label}
                        {showGeoTooltip && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="dyna-tier__geo-star"
                                aria-label={t("dyna.pricing.geo_tooltip")}
                              >
                                ⭐
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {t("dyna.pricing.geo_tooltip")}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <button
                type="button"
                className={`dyna-btn${isFeatured ? "" : " dyna-btn--on-cream"}`}
                onClick={() => handleCTA(tier.key)}
              >
                {ctaLabel} <span className="dyna-arrow">→</span>
              </button>
            </article>
          );
        })}
      </div>

      <NicheModal
        tier={modalTier}
        open={modalTier !== null}
        onClose={() => setModalTier(null)}
      />
    </section>
  );
}
